import type { DraftRequest, PipelineStage, ParamValues, StageSavingsReport } from './types';
import { draftText } from './draft-text';
import { estimateTokens } from './token-estimate';

export interface StageRunOutcome {
  stageId: string;
  ran: boolean;
  skippedReason?: 'disabled' | 'time-budget-exceeded' | 'error-failed-open';
  report?: StageSavingsReport;
  warning?: string;
}

export interface PipelineRunReport {
  stages: StageRunOutcome[];
  totalTokensBefore: number;
  totalTokensAfter: number;
  totalTokensSaved: number;
  timedOut: boolean;
  elapsedMs: number;
}

export interface PipelineRunResult {
  draft: DraftRequest;
  report: PipelineRunReport;
}

/**
 * D2 (prompt caching structure) is cheap, pure reordering — it always runs,
 * even if the time budget has otherwise been exhausted, since skipping it
 * would leave the request in an inconsistent shape rather than just less
 * optimized.
 */
const ALWAYS_RUN_STAGES = new Set(['D2']);

export async function runPipeline(
  stages: PipelineStage[],
  enabledStages: Record<string, boolean>,
  params: Record<string, ParamValues>,
  initialDraft: DraftRequest,
  budgetMs = 2000,
  summarizeWithModel?: (prompt: string, signal: AbortSignal) => Promise<string>,
): Promise<PipelineRunResult> {
  const startedAt = performance.now();
  const controller = new AbortController();
  const deadline = startedAt + budgetMs;

  let draft = initialDraft;
  const initialTokens = estimateTokens(draftText(initialDraft));
  let runningTokens = initialTokens;
  const outcomes: StageRunOutcome[] = [];
  let timedOut = false;

  for (const stage of stages) {
    const enabled = enabledStages[stage.id] ?? stage.defaultEnabled;
    if (!enabled) {
      outcomes.push({ stageId: stage.id, ran: false, skippedReason: 'disabled' });
      continue;
    }

    const overBudget = performance.now() >= deadline;
    if (overBudget && !ALWAYS_RUN_STAGES.has(stage.id)) {
      timedOut = true;
      outcomes.push({ stageId: stage.id, ran: false, skippedReason: 'time-budget-exceeded' });
      continue;
    }
    if (overBudget) controller.abort();

    const stageParams = { ...defaultParams(stage), ...params[stage.id] };

    try {
      const result = await stage.run(draft, stageParams, {
        estimateTokens,
        signal: controller.signal,
        summarizeWithModel,
      });
      draft = result.draft;
      runningTokens = result.report.tokensAfter;
      outcomes.push({ stageId: stage.id, ran: true, report: result.report, warning: result.warning });
    } catch (err) {
      // Fail open: never let one stage's bug block the request. Skip it and
      // surface a visible warning instead of throwing.
      outcomes.push({
        stageId: stage.id,
        ran: false,
        skippedReason: 'error-failed-open',
        warning: err instanceof Error ? err.message : String(err),
      });
    }
  }

  const elapsedMs = performance.now() - startedAt;
  const totalTokensSaved = Math.max(0, initialTokens - runningTokens);

  return {
    draft,
    report: {
      stages: outcomes,
      totalTokensBefore: initialTokens,
      totalTokensAfter: runningTokens,
      totalTokensSaved,
      timedOut,
      elapsedMs,
    },
  };
}

function defaultParams(stage: PipelineStage): ParamValues {
  if (!stage.params) return {};
  const out: ParamValues = {};
  for (const [key, spec] of Object.entries(stage.params)) out[key] = spec.default;
  return out;
}
